import React, { useEffect, useMemo, useRef, useState } from "react";
import TablePagination from "../../components/shared/TablePagination";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Modal } from "react-bootstrap";
import { getApiErrorMessage } from "../../store/api/baseApi";
import { exportToExcel } from "../../utils/exportExcel";
import {
  RequestRecord,
  useDeleteRequestMutation,
  useFetchRequestsQuery,
  useUpdateRequestMutation,
} from "../../store/api/requestsApi";
import {
  useSendRequestEmailMutation,
  useSendRequestSmsMutation,
  useSendRequestWhatsAppMutation,
} from "../../store/api/notificationsApi";
import "./admin.css";

type RequestStatus = "pending" | "in-progress" | "completed";

type EditableRequest = {
  name: string;
  phone: string;
  email: string;
  city: string;
  projectType: string;
  message: string;
  status: RequestStatus;
};

const PAGE_SIZE = 5;

const normalizeEditableStatus = (status: string): RequestStatus => {
  if (status === "in-progress" || status === "completed") {
    return status;
  }

  return "pending";
};

const toEditableRequest = (request: RequestRecord): EditableRequest => ({
  name: request.name ?? "",
  phone: request.phone ?? "",
  email: request.email ?? "",
  city: request.city ?? "",
  projectType: request.projectType ?? "",
  message: request.message ?? "",
  status: normalizeEditableStatus(request.status),
});

const getStatusBadgeClass = (status: string) => {
  if (status === "pending") {
    return "text-bg-warning";
  }

  if (status === "in-progress") {
    return "text-bg-primary";
  }

  return "text-bg-success";
};

const getStatusLabel = (status: string) => {
  if (status === "in-progress") {
    return "In Progress";
  }

  if (status === "completed") {
    return "Completed";
  }

  return "Pending";
};

const getPhoneHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, "")}`;

type NotifyChannel = "email" | "sms" | "whatsapp";

const RequestManager = () => {
  const { data = [], isLoading, error } = useFetchRequestsQuery();
  const [updateRequest, { isLoading: isUpdating }] = useUpdateRequestMutation();
  const [deleteRequest, { isLoading: isDeleting }] = useDeleteRequestMutation();
  const [sendEmail, { isLoading: isSendingEmail }] = useSendRequestEmailMutation();
  const [sendSms, { isLoading: isSendingSms }] = useSendRequestSmsMutation();
  const [sendWhatsApp, { isLoading: isSendingWhatsApp }] = useSendRequestWhatsAppMutation();
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<EditableRequest | null>(null);
  const [previewRequest, setPreviewRequest] = useState<RequestRecord | null>(null);
  const [actionError, setActionError] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  // ── Notify modal state ───────────────────────────────────────────────────
  const [notifyRequest, setNotifyRequest] = useState<RequestRecord | null>(null);
  const [notifyFeedback, setNotifyFeedback] = useState<Record<NotifyChannel, { state: "idle" | "sending" | "ok" | "err"; msg: string }>>({
    email: { state: "idle", msg: "" },
    sms:   { state: "idle", msg: "" },
    whatsapp: { state: "idle", msg: "" },
  });

  const openNotifyModal = (req: RequestRecord) => {
    setNotifyRequest(req);
    setNotifyFeedback({ email: { state: "idle", msg: "" }, sms: { state: "idle", msg: "" }, whatsapp: { state: "idle", msg: "" } });
  };

  const handleNotifyChannel = async (channel: NotifyChannel) => {
    if (!notifyRequest) return;
    setNotifyFeedback((prev) => ({ ...prev, [channel]: { state: "sending", msg: "" } }));
    try {
      const id = notifyRequest.id;
      if (channel === "email")     await sendEmail(id).unwrap();
      if (channel === "sms")       await sendSms(id).unwrap();
      if (channel === "whatsapp")  await sendWhatsApp(id).unwrap();
      setNotifyFeedback((prev) => ({ ...prev, [channel]: { state: "ok", msg: "Sent!" } }));
    } catch {
      setNotifyFeedback((prev) => ({ ...prev, [channel]: { state: "err", msg: "Failed" } }));
    }
  };

  const requests = useMemo<RequestRecord[]>(() => data, [data]);

  const filteredRequests = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return requests.filter((request) => {
      const matchesStatus =
        statusFilter === "all" || request.status === statusFilter;
      const matchesQuery =
        !query ||
        request.name.toLowerCase().includes(query) ||
        request.phone.toLowerCase().includes(query) ||
        request.email.toLowerCase().includes(query) ||
        request.message.toLowerCase().includes(query) ||
        request.projectType.toLowerCase().includes(query);

      return matchesStatus && matchesQuery;
    });
  }, [requests, searchQuery, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / PAGE_SIZE));
  const errorMessage = error ? getApiErrorMessage(error as FetchBaseQueryError) : "";

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!editingRequestId) {
      return;
    }

    const activeRequest = requests.find((request) => request.id === editingRequestId);
    if (!activeRequest) {
      setEditingRequestId(null);
      setEditingValues(null);
    }
  }, [editingRequestId, requests]);

  useEffect(() => {
    if (!previewRequest) {
      return;
    }

    const activeRequest = requests.find((request) => request.id === previewRequest.id);
    if (!activeRequest) {
      setPreviewRequest(null);
    }
  }, [previewRequest, requests]);

  const pagedRequests = filteredRequests.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const pendingCount = requests.filter((item) => item.status === "pending").length;
  const activeCount = requests.filter((item) => item.status === "in-progress").length;
  const completedCount = requests.filter((item) => item.status === "completed").length;

  const openEditModal = (request: RequestRecord) => {
    setEditingRequestId(request.id);
    setEditingValues(toEditableRequest(request));
    setActionError("");
    setActionMessage("");
  };

  const closeEditModal = () => {
    setEditingRequestId(null);
    setEditingValues(null);
  };

  const handleFieldChange = (field: keyof EditableRequest, value: string) => {
    setEditingValues((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleSave = async () => {
    if (!editingRequestId || !editingValues) {
      return;
    }

    setActionError("");
    setActionMessage("");

    try {
      const response = await updateRequest({
        id: editingRequestId,
        ...editingValues,
      }).unwrap();

      setActionMessage(response.message || "Request updated successfully.");
      closeEditModal();
    } catch (apiError) {
      setActionError(getApiErrorMessage(apiError as FetchBaseQueryError));
    }
  };

  const handleDelete = async (id: string) => {
    const shouldDelete = window.confirm(
      "Are you sure you want to delete this request? This action cannot be undone.",
    );

    if (!shouldDelete) {
      return;
    }

    setActionError("");
    setActionMessage("");

    try {
      const response = await deleteRequest(id).unwrap();
      setActionMessage(response.message || "Request deleted successfully.");
      if (editingRequestId === id) {
        closeEditModal();
      }
      if (previewRequest?.id === id) {
        setPreviewRequest(null);
      }
    } catch (apiError) {
      setActionError(getApiErrorMessage(apiError as FetchBaseQueryError));
    }
  };

  const isBusy = isUpdating || isDeleting;

  return (
    <section className='admin-page'>
      <div className='container-fluid px-0'>
        <div className='row g-4'>
          <div className='col-12'>
            <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>
              {/* Gradient header */}
              <div style={{ background: "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className='bi bi-inbox-fill' style={{ color: "#fff", fontSize: 14 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>Request Management</div>
                    <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>Review enquiries and update client request records</div>
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                  <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 600 }}>{requests.length} Total</span>
                  <span style={{ background: "#fef3c7", color: "#b45309", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700 }}>{pendingCount} Pending</span>
                  <span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700 }}>{activeCount} In Progress</span>
                  <span style={{ background: "#dcfce7", color: "#15803d", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700 }}>{completedCount} Completed</span>
                </div>
              </div>
              {/* Filter bar */}
              <div className='d-flex flex-wrap gap-3 align-items-center p-3 border-bottom'>
                <div style={{ minWidth: 240 }}>
                  <input
                    className='form-control form-control-sm'
                    placeholder='Search by name, phone, email, or type…'
                    title='Search requests'
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </div>
                <div style={{ minWidth: 160 }}>
                  <select
                    className='form-select form-select-sm'
                    value={statusFilter}
                    title='Filter by status'
                    aria-label='Filter by status'
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    <option value='all'>All Statuses</option>
                    <option value='pending'>Pending</option>
                    <option value='in-progress'>In Progress</option>
                    <option value='completed'>Completed</option>
                  </select>
                </div>
                <button type='button' className='btn btn-outline-success btn-sm d-inline-flex align-items-center gap-2'
                  onClick={() => exportToExcel(filteredRequests.map((r) => ({ Name: r.name, Email: r.email, Phone: r.phone, City: r.city, "Project Type": r.projectType, Message: r.message, Status: getStatusLabel(r.status), Date: r.createdAt })), "Requests")}>
                  <i className='bi bi-file-earmark-excel' /> Export
                </button>
                <span className='small text-muted ms-auto'>
                  {filteredRequests.length} request{filteredRequests.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className='card-body p-4'>

                {isLoading ? <div className='alert alert-light border'>Loading requests...</div> : null}
                {errorMessage ? <div className='alert alert-danger'>{errorMessage}</div> : null}
                {actionError ? <div className='alert alert-danger'>{actionError}</div> : null}
                {actionMessage ? <div className='alert alert-success'>{actionMessage}</div> : null}

                {!isLoading && !errorMessage ? (
                  <>
                    <div className='request-table-shell d-none d-lg-block'>
                      <div className='table-responsive'>
                        <table className='table align-middle mb-0 admin-request-table'>
                          <thead>
                            <tr>
                              <th scope='col'>#</th>
                              <th scope='col'>Client</th>
                              <th scope='col'>Mobile</th>
                              <th scope='col'>Requirement</th>
                              <th scope='col' className='request-date-col'>Date</th>
                              <th scope='col' className='request-status-col'>Status</th>
                              <th scope='col' className='request-action-col'>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedRequests.map((request, idx) => (
                              <tr key={request.id}>
                                <td className='text-muted'>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                                <td>
                                  <div className='request-primary'>{request.name || "Unknown Client"}</div>
                                  <div className='request-meta-line'>{request.email || "No email provided"}</div>
                                  <div className='request-meta-line'>ID: {request.id}</div>
                                </td>
                                <td>
                                  <a className='request-phone-chip' href={getPhoneHref(request.phone || "")}>
                                    {request.phone || "No phone"}
                                  </a>
                                  {request.city ? (
                                    <div className='request-meta-line mt-2'>{request.city}</div>
                                  ) : null}
                                </td>
                                <td>
                                  <div className='request-primary mb-1'>{request.projectType || "General"}</div>
                                  <button
                                    type='button'
                                    className='btn btn-link btn-sm request-link-btn p-0'
                                    onClick={() => setPreviewRequest(request)}
                                  >
                                    View requirement
                                  </button>
                                </td>
                                <td className='request-date-col'>
                                  <div className='request-date-text'>{request.createdAt || "-"}</div>
                                </td>
                                <td className='request-status-col'>
                                  <span className={`badge rounded-pill px-3 py-2 ${getStatusBadgeClass(request.status)}`}>
                                    {getStatusLabel(request.status)}
                                  </span>
                                </td>
                                <td className='request-action-col'>
                                  <div className='request-action-stack request-action-stack-inline'>
                                    <button
                                      type='button'
                                      className='btn btn-outline-primary btn-sm admin-icon-btn'
                                      onClick={() => openEditModal(request)}
                                      disabled={isBusy}
                                      aria-label={`Update ${request.name || request.id}`}
                                      title='Update request'
                                    >
                                      <i className='bi bi-pencil-square'></i>
                                    </button>
                                    <button
                                      type='button'
                                      className='btn btn-sm admin-icon-btn'
                                      style={{ background: "linear-gradient(135deg,#0d9488,#0f766e)", color: "#fff", border: "none" }}
                                      onClick={() => openNotifyModal(request)}
                                      title='Send notification'
                                      aria-label={`Send notification to ${request.name || request.id}`}
                                    >
                                      <i className='bi bi-send'></i>
                                    </button>
                                    <button
                                      type='button'
                                      className='btn btn-outline-danger btn-sm admin-icon-btn'
                                      onClick={() => handleDelete(request.id)}
                                      disabled={isBusy}
                                      aria-label={`Delete ${request.name || request.id}`}
                                      title='Delete request'
                                    >
                                      <i className='bi bi-trash'></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className='d-grid gap-3 d-lg-none'>
                      {pagedRequests.map((request) => (
                        <article key={request.id} className='admin-mobile-request card border-0 shadow-sm rounded-4'>
                          <div className='card-body p-3'>
                            <div className='d-flex justify-content-between align-items-start gap-3 mb-3'>
                              <div>
                                <h3 className='h6 mb-1'>{request.name || "Unknown Client"}</h3>
                                <div className='small text-muted'>ID: {request.id}</div>
                              </div>
                              <span className={`badge rounded-pill px-3 py-2 ${getStatusBadgeClass(request.status)}`}>
                                {getStatusLabel(request.status)}
                              </span>
                            </div>
                            <div className='request-mobile-grid'>
                              <div>
                                <div className='request-label'>Mobile</div>
                                <a className='request-phone-chip mt-1' href={getPhoneHref(request.phone || "")}>
                                  {request.phone || "No phone"}
                                </a>
                              </div>
                              <div>
                                <div className='request-label'>Date</div>
                                <div>{request.createdAt || "-"}</div>
                              </div>
                              <div>
                                <div className='request-label'>Email</div>
                                <div className='request-break'>{request.email || "-"}</div>
                              </div>
                              <div>
                                <div className='request-label'>Project Type</div>
                                <div>{request.projectType || "General"}</div>
                              </div>
                            </div>
                            {request.city ? (
                              <div className='mt-3'>
                                <div className='request-label'>City</div>
                                <div>{request.city}</div>
                              </div>
                            ) : null}
                            <div className='d-flex flex-wrap gap-2 mt-3'>
                              <button
                                type='button'
                                className='btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2'
                                onClick={() => setPreviewRequest(request)}
                              >
                                <i className='bi bi-eye'></i>
                                <span>View</span>
                              </button>
                              <button
                                type='button'
                                className='btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2'
                                onClick={() => openEditModal(request)}
                                disabled={isBusy}
                              >
                                <i className='bi bi-pencil-square'></i>
                                <span>Update</span>
                              </button>
                              <button
                                type='button'
                                className='btn btn-sm d-inline-flex align-items-center gap-2'
                                style={{ background: "linear-gradient(135deg,#0d9488,#0f766e)", color: "#fff", border: "none" }}
                                onClick={() => openNotifyModal(request)}
                              >
                                <i className='bi bi-send'></i>
                                <span>Notify</span>
                              </button>
                              <button
                                type='button'
                                className='btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-2'
                                onClick={() => handleDelete(request.id)}
                                disabled={isBusy}
                              >
                                <i className='bi bi-trash'></i>
                                <span>Delete</span>
                              </button>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>

                    {filteredRequests.length === 0 ? (
                      <div className='alert alert-light border mt-3 mb-0'>
                        No requests match the current filters.
                      </div>
                    ) : null}

                    <TablePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={filteredRequests.length}
                      pageSize={PAGE_SIZE}
                      onPageChange={setCurrentPage}
                    />
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Requirement Details Modal ──────────────────────────── */}
      <Modal show={Boolean(previewRequest)} onHide={() => setPreviewRequest(null)} centered>

        <Modal.Header closeButton closeVariant="white" className="req-modal-header">
          <Modal.Title className="req-modal-title">
            <span className="req-modal-title-icon">
              <i className='bi bi-file-text-fill' />
            </span>
            Requirement Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className='p-0'>

          {/* Client banner */}
          <div className="req-modal-client-banner">
            <div className="req-modal-client-avatar">
              {(previewRequest?.name ?? "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="req-modal-client-name">{previewRequest?.name || "Unknown Client"}</div>
              <div className="req-modal-client-type">{previewRequest?.projectType || "General Enquiry"}</div>
            </div>
            <span className={`badge rounded-pill px-3 py-2 ms-auto ${getStatusBadgeClass(previewRequest?.status ?? "pending")}`}>
              {getStatusLabel(previewRequest?.status ?? "pending")}
            </span>
          </div>

          {/* Detail rows */}
          <div className="req-modal-body">

            {/* Phone + City */}
            <div className="req-modal-grid-2">
              <div className="req-modal-info-cell">
                <div className="req-modal-info-label">Phone</div>
                <a href={`tel:${(previewRequest?.phone ?? "").replace(/[^\d+]/g, "")}`} className="req-modal-info-value">
                  <i className='bi bi-telephone-fill' style={{ color: "#10b981" }} />
                  {previewRequest?.phone || "—"}
                </a>
              </div>
              <div className="req-modal-info-cell">
                <div className="req-modal-info-label">City</div>
                <div className="req-modal-info-value">
                  <i className='bi bi-geo-alt-fill' style={{ color: "#0ea5e9" }} />
                  {previewRequest?.city || "—"}
                </div>
              </div>
            </div>

            {previewRequest?.email && (
              <div className="req-modal-email-row">
                <i className='bi bi-envelope-fill' style={{ color: "#f59e0b", flexShrink: 0 }} />
                {previewRequest.email}
              </div>
            )}

            {previewRequest?.createdAt && (
              <div className="req-modal-date-row">
                <i className='bi bi-calendar3' />
                Submitted on {previewRequest.createdAt}
              </div>
            )}

            {/* Message */}
            <div>
              <div className="req-modal-message-section-label">
                <i className='bi bi-chat-left-text-fill' style={{ color: "#0d9488" }} />
                Requirement Message
              </div>
              <div className="req-modal-message-box">
                {previewRequest?.message || "No requirement message provided."}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="req-modal-footer">
          <button type="button" className="req-modal-btn-close" onClick={() => setPreviewRequest(null)}>
            Close
          </button>
          <button
            type="button"
            className="req-modal-btn-edit"
            onClick={() => {
              const req = requests.find((r) => r.id === previewRequest?.id);
              setPreviewRequest(null);
              if (req) openEditModal(req);
            }}
          >
            <i className='bi bi-pencil-square' /> Edit Request
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Send Notification Modal ─────────────────────────────────── */}
      <Modal show={Boolean(notifyRequest)} onHide={() => setNotifyRequest(null)} centered size='sm'>
        <Modal.Body style={{ padding: 0 }}>
          {notifyRequest && (() => {
            const hasEmail = Boolean(notifyRequest.email?.trim());
            const hasPhone = Boolean(notifyRequest.phone?.trim());
            const channels: { key: NotifyChannel; icon: string; label: string; color: string; bg: string; disabled: boolean; disabledHint: string }[] = [
              { key: "email",    icon: "bi bi-envelope-fill",  label: "Send Email",    color: "#1d4ed8", bg: "#eff6ff", disabled: !hasEmail,  disabledHint: "No email on record" },
              { key: "sms",      icon: "bi bi-phone-fill",     label: "Send SMS",      color: "#0d9488", bg: "#f0fdfa", disabled: !hasPhone,  disabledHint: "No phone on record" },
              { key: "whatsapp", icon: "bi bi-whatsapp",       label: "Send WhatsApp", color: "#16a34a", bg: "#f0fdf4", disabled: !hasPhone,  disabledHint: "No phone on record" },
            ];
            return (
              <>
                {/* Hero */}
                <div style={{ background: "linear-gradient(135deg,#0c2420,#134e4a,#0d9488)", padding: "16px 20px 14px", borderRadius: "8px 8px 0 0", position: "relative" }}>
                  <button type='button' aria-label='Close' title='Close' onClick={() => setNotifyRequest(null)} style={{ position: "absolute", top: 10, right: 12, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 6, width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
                    <i className='bi bi-x-lg' style={{ fontSize: "0.75rem" }} />
                  </button>
                  <div style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.55)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
                    <i className='bi bi-send-fill' style={{ marginRight: 5 }} />Send Notification
                  </div>
                  <div style={{ color: "#fff", fontWeight: 800, fontSize: "0.95rem" }}>{notifyRequest.name}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    {hasEmail && <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.7)", display: "flex", alignItems: "center", gap: 4 }}><i className='bi bi-envelope' />{notifyRequest.email}</span>}
                    {hasPhone && <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.7)", display: "flex", alignItems: "center", gap: 4 }}><i className='bi bi-phone' />{notifyRequest.phone}</span>}
                  </div>
                </div>

                {/* Channel buttons */}
                <div style={{ padding: "16px 18px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {channels.map(({ key, icon, label, color, bg, disabled, disabledHint }) => {
                    const fb = notifyFeedback[key];
                    const sending = fb.state === "sending";
                    return (
                      <div key={key}>
                        <button
                          type='button'
                          disabled={disabled || sending || isSendingEmail || isSendingSms || isSendingWhatsApp}
                          onClick={() => handleNotifyChannel(key)}
                          title={disabled ? disabledHint : label}
                          style={{
                            width: "100%", display: "flex", alignItems: "center", gap: 10,
                            background: disabled ? "#f8fafc" : bg,
                            border: `1.5px solid ${disabled ? "#e2e8f0" : color}22`,
                            borderRadius: 10, padding: "10px 14px", cursor: disabled ? "not-allowed" : "pointer",
                            opacity: disabled ? 0.5 : 1, transition: "all 0.15s",
                          }}
                        >
                          <span style={{ width: 32, height: 32, borderRadius: 8, background: disabled ? "#e2e8f0" : `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {sending
                              ? <span className='spinner-border spinner-border-sm' style={{ color, width: 14, height: 14, borderWidth: 2 }} />
                              : <i className={icon} style={{ color: disabled ? "#94a3b8" : color, fontSize: "0.9rem" }} />}
                          </span>
                          <span style={{ flex: 1, textAlign: "left", fontWeight: 600, fontSize: "0.84rem", color: disabled ? "#94a3b8" : "#0f172a" }}>{label}</span>
                          {fb.state === "ok"  && <span style={{ fontSize: "0.72rem", color: "#16a34a", fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}><i className='bi bi-check-circle-fill' /> Sent</span>}
                          {fb.state === "err" && <span style={{ fontSize: "0.72rem", color: "#dc2626", fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}><i className='bi bi-x-circle-fill' /> Failed</span>}
                          {disabled && <span style={{ fontSize: "0.68rem", color: "#94a3b8" }}>{disabledHint}</span>}
                        </button>
                      </div>
                    );
                  })}

                  <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 10, marginTop: 2, fontSize: "0.72rem", color: "#94a3b8", textAlign: "center" }}>
                    Each channel is sent independently · failures are logged
                  </div>
                </div>
              </>
            );
          })()}
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(editingRequestId && editingValues)}
        onHide={closeEditModal}
        centered
        size='lg'
      >
        <Modal.Header closeButton closeVariant="white" style={{ background: "linear-gradient(135deg, #78350f, #d97706, #f59e0b)", border: "none", padding: "10px 20px" }}>
          <Modal.Title style={{ color: "#fff", fontWeight: 700, fontSize: "0.92rem", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className='bi bi-pencil-square' style={{ color: "#fff", fontSize: 13 }} />
            </div>
            Update Request
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingValues ? (
            <div className='row g-3'>
              <div className='col-md-6'>
                <div style={{ borderLeft: "3px solid #6366f1", paddingLeft: 10 }}>
                  <label className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Client Name</label>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text' style={{ background: "#eef2ff", borderColor: "#c7d2fe", color: "#6366f1" }}><i className='bi bi-person-fill' style={{ fontSize: 12 }} /></span>
                    <input className='form-control form-control-sm' style={{ borderColor: "#c7d2fe", boxShadow: "none" }} title='Client name' value={editingValues.name} onChange={(e) => handleFieldChange("name", e.target.value)} />
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div style={{ borderLeft: "3px solid #10b981", paddingLeft: 10 }}>
                  <label className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Phone Number</label>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text' style={{ background: "#d1fae5", borderColor: "#a7f3d0", color: "#059669" }}><i className='bi bi-telephone-fill' style={{ fontSize: 12 }} /></span>
                    <input className='form-control form-control-sm' style={{ borderColor: "#a7f3d0", boxShadow: "none" }} title='Phone number' value={editingValues.phone} onChange={(e) => handleFieldChange("phone", e.target.value)} />
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div style={{ borderLeft: "3px solid #f59e0b", paddingLeft: 10 }}>
                  <label className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Email Address</label>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text' style={{ background: "#fef3c7", borderColor: "#fcd34d", color: "#d97706" }}><i className='bi bi-envelope-fill' style={{ fontSize: 12 }} /></span>
                    <input className='form-control form-control-sm' style={{ borderColor: "#fcd34d", boxShadow: "none" }} title='Email address' value={editingValues.email} onChange={(e) => handleFieldChange("email", e.target.value)} />
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div style={{ borderLeft: "3px solid #0ea5e9", paddingLeft: 10 }}>
                  <label className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>City</label>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text' style={{ background: "#e0f2fe", borderColor: "#bae6fd", color: "#0ea5e9" }}><i className='bi bi-geo-alt-fill' style={{ fontSize: 12 }} /></span>
                    <input className='form-control form-control-sm' style={{ borderColor: "#bae6fd", boxShadow: "none" }} title='City' value={editingValues.city} onChange={(e) => handleFieldChange("city", e.target.value)} />
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div style={{ borderLeft: "3px solid #8b5cf6", paddingLeft: 10 }}>
                  <label className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Project Type</label>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text' style={{ background: "#ede9fe", borderColor: "#ddd6fe", color: "#7c3aed" }}><i className='bi bi-building' style={{ fontSize: 12 }} /></span>
                    <input className='form-control form-control-sm' style={{ borderColor: "#ddd6fe", boxShadow: "none" }} title='Project type' value={editingValues.projectType} onChange={(e) => handleFieldChange("projectType", e.target.value)} />
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div style={{ borderLeft: "3px solid #f43f5e", paddingLeft: 10 }}>
                  <label className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Workflow Status</label>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text' style={{ background: "#ffe4e6", borderColor: "#fecdd3", color: "#e11d48" }}><i className='bi bi-check-circle-fill' style={{ fontSize: 12 }} /></span>
                    <select className='form-select form-select-sm' style={{ borderColor: "#fecdd3", boxShadow: "none" }} title='Workflow status' aria-label='Workflow status' value={editingValues.status} onChange={(e) => handleFieldChange("status", e.target.value as RequestStatus)}>
                      <option value='pending'>Pending</option>
                      <option value='in-progress'>In Progress</option>
                      <option value='completed'>Completed</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className='col-12'>
                <div style={{ borderLeft: "3px solid #94a3b8", paddingLeft: 10 }}>
                  <label className='form-label mb-1 fw-semibold' style={{ fontSize: "0.84rem", color: "#334155" }}>Requirement Message</label>
                  <textarea className='form-control form-control-sm' style={{ borderColor: "#cbd5e1", boxShadow: "none" }} rows={4} title='Requirement message' value={editingValues.message} onChange={(e) => handleFieldChange("message", e.target.value)} />
                </div>
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1.5px dashed #e2e8f0", background: "#f8fafc" }}>
          <button type='button' style={{ background: "#f1f5f9", border: "1.5px solid #cbd5e1", color: "#64748b", borderRadius: 8, padding: "6px 16px", fontSize: "0.83rem", fontWeight: 600, cursor: "pointer" }} onClick={closeEditModal} disabled={isBusy}>Cancel</button>
          <button type='button' disabled={isBusy} style={{ background: "linear-gradient(135deg, #d97706, #f59e0b)", border: "none", color: "#fff", borderRadius: 8, padding: "7px 18px", fontSize: "0.83rem", fontWeight: 700, cursor: isBusy ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 7, boxShadow: "0 3px 10px #d9770633" }} onClick={handleSave}>
            {isUpdating ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> Saving…</> : <><i className='bi bi-check2-square' /> Save Changes</>}
          </button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default RequestManager;

